import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
    addToCart,
    addMultipleToCart,
    launchCartFlyout,
    addCTOToCart,
    updateCart,
    updateCartBySKU,
} from '../utility/utility-actions';
import { confirmAddToCart } from '../utility/components/add-to-cart-confirm/add-to-cart-confirm-actions';
import { fetchXSellProducts } from '../xsell/xsell-actions';
import { fetchPrices } from '../product/product-actions';
import PurchaseModal from '../product/components/purchase-modal';
import { Helpers } from '../core/src/helpers';
import useProductPrice from '../hooks/useProductPrice';
import usePdpCta from '../hooks/usePdpCta';

const EMPTY_OBJECT = {};
const suppliesSearchUrl = `${process.env.BASENAME}/cv/supplies-search`;

/**
 * Higher-Order Component that wraps a child component with PurchaseModal and purchase logic.
 * Uses the usePdpCta hook to manage add-to-cart and purchase flows.
 *
 * @param {React.Component} ChildComponent - The component to wrap; receives ctaFunction prop
 * @returns {React.Component} Wrapped component with PurchaseModal and purchase context
 *
 * @description
 * - Uses usePdpCta hook for all purchase/add-to-cart logic
 * - Wraps child with PurchaseModal component for cross-sell/xsell flows
 * - Uses render props pattern to pass modal context (openModal, customModalData, onPurchase)
 * - Calls wrapHandlers(modalProps) to bind modal context to handlers
 * - Passes only ctaFunction to child component; all handlers are bound within PurchaseModal render props
 *
 * @example
 * const MyComponent = ({ ctaFunction, ...props }) => (
 *   <button onClick={ctaFunction} disabled={props.btnBusy}>
 *     Add to Cart
 *   </button>
 * );
 *
 * export default withPurchaseModal(MyComponent);
 *
 * @prop {Function} ctaFunction - Ready-to-use onClick handler that:
 *   - Checks for CTO link and prevents default if needed
 *   - Wraps custom onClick callbacks from parent
 *   - Executes purchase or openCustomModal based on modal context
 */
const withPurchaseModal = ChildComponent => {
    return props => {
        // Redux state selectors
        const { productData, etrStoreData, userData, xSell, cartInfo, productInitial } = useSelector(state => ({
            productData: state.productData,
            etrStoreData: state.etrStoreData,
            userData: state.userData,
            xSell: state.xSell,
            cartInfo: state.cartInfo,
            productInitial: state?.slugInfo?.components?.productInitial,
        }));

        // Redux dispatch
        const dispatch = useDispatch();

        // Dispatch actions
        const dispatchActions = {
            updateCart: (item, qty) => dispatch(updateCart(item, qty)),
            updateCartBySKU: (sku, qty) => dispatch(updateCartBySKU(sku, qty)),
            addCTOToCart: (product, quantity, blockFlyout, followRedirect) =>
                dispatch(addCTOToCart(product, quantity, blockFlyout, followRedirect)),
            addToCart: (product, quantity, blockFlyout, addOns, options, baseProductSku, xsellType, metricParams) =>
                dispatch(
                    addToCart(product, quantity, blockFlyout, addOns, options, baseProductSku, xsellType, metricParams),
                ),
            addMultipleToCart: (products, quantity, blockFlyout, options, baseProductSku, xsellType) =>
                dispatch(addMultipleToCart(products, quantity, blockFlyout, options, baseProductSku, xsellType)),
            fetchXSellProducts: (product, limit, source, isV2) =>
                dispatch(fetchXSellProducts(product, limit, source, isV2)),
            fetchPrices: Helpers.memoize(id => dispatch(fetchPrices([id]))),
            confirmAddToCart: (confirmType, params) => dispatch(confirmAddToCart(confirmType, params)),
            launchCartFlyout: payload => dispatch(launchCartFlyout(payload)),
        };

        // Derived state from Redux
        const { productInfo, ctoConfigurations } = productData || {};
        const { labels: priceLabels } = etrStoreData || {};

        // Props from parent component
        const {
            product: productProp,
            customModalBlocked = false,
            isV2 = false,
            templateKey = 'pdp',
            jumpid,
            quantity = 1,
            showSelector = false,
        } = props;

        const product = productProp || productInitial;

        // Get price from Redux state
        const { prices } = useProductPrice([product]);
        let price = Helpers.getProductPrice(product, productInfo?.productPrices, true, ctoConfigurations);
        if (!price || Object.keys(price).length === 0) {
            price = (product && prices[product.sku]) || {};
        }

        // Use hook to get purchase/modal CTA functions and state
        const { purchase, openCustomModal, wrapHandlers, btnBusy } = usePdpCta({
            baseProductSku: props.baseProductSku,
            xsellType: props.xsellType,
            onClick: props.onClick,
            jumpid,
            additionalParams: props.additionalParams,
            history: props.history,
            isMultiple: props.isMultiple,
            qty: quantity,
            customModalBlocked,
        });

        // PurchaseModal props
        const purchaseModalProps = {
            jumpid,
            product,
            price,
            quantity,
            addToCart: dispatchActions.addToCart,
            customModalBlocked,
            fetchXSellProducts: dispatchActions.fetchXSellProducts,
            launchCartFlyout: dispatchActions.launchCartFlyout,
            isV2,
            templateKey,
        };

        return (
            <PurchaseModal {...purchaseModalProps}>
                {modalProps => {
                    // Get wrapped handlers that include modal props
                    const { ctaFunction } = wrapHandlers(modalProps);

                    return <ChildComponent {...props} ctaFunction={ctaFunction} />;
                }}
            </PurchaseModal>
        );
    };
};

export default withPurchaseModal;
